import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { environment } from 'src/environments/environment';
import { NgForm } from '@angular/forms';

interface LawyerResponse {
  lawyer_id: string;
  id: string;
  name: string;
  licenseNumber: string;
  bio: string;
  yearsOfExperience: number;
  consultationFee: number;
  availability: string;
  profilePicUrl: string;
  language: string[];
  specializations: string[];
  subspecializations: string[];
  address: {
    area: string;
    street: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
    formatted: string;
  };
  geo: { lat: number; lon: number };
  planType: string;
  // NEW: firm fields
  isFirm?: boolean;
  memberCount?: number;
  firmExperience?: number;
}

interface LawyerDisplay {
  lawyer_id: string;
  name: string;
  avatar: string;
  specialty: string;
  rating: number;
  reviews: number;
  experience: number;
  location: string;
  languages: string;
  availability: string;
  availabilityStyle: { background: string; color: string };
  consultationFee: number;
  bio: string;
  id: string;
  specializations: string[];
  isAvailableToday: boolean;
  isAvailableTomorrow: boolean;
  isAvailableThisWeek: boolean;
  isSubActive?: boolean;

  // NEW: firm-specific fields
  isFirm?: boolean;
  memberCount?: number;
  firmId?: string;
  address?: any;
  firmExperience?: number;
}

@Component({
    selector: 'app-search-results',
    imports: [CommonModule, FormsModule],
    templateUrl: './search-results.component.html',
    styleUrls: ['./search-results.component.css']
})
export class SearchResultsComponent {

  @ViewChild('resultsList') resultsListRef!: ElementRef;

  mobileMenuOpen = false;
  isLoading = false;

  filters = {
    searchQuery: '',
    location: '',
    experience: '',
    rating: ''
  };

  advancedFilters = {
    expertise: '',
    language: '',
    fees: '',
    availability: ''
  };

  lawyers: LawyerDisplay[] = [];
  allLawyers: LawyerDisplay[] = [];
  sortOption = 'Highest Rated';
  currentYear = new Date().getFullYear();

  availableExpertise: string[] = [];
  availableLanguages: string[] = [];
  data: any;
  lawyer: any;
  profileImg: string | null = null;

  relNextCursor: any;
  relPrevCursor: any;
  popularSpecializations: any[] = [];
  selectedSpecialization: any = null;

  initialSearchCriteria: any = null;
  totalCount: number = 0;

  isLoggedIn = false;
  profileImage: string = '';
  firstName: string = '';
  showSpecializationDropdown = false;
  filteredSpecializations: any[] = [];

  showSubscriptionPopup = false;
  selectedLawyer: any = null;
  leadForm = {
    name: '',
    email: '',
    mobile: '',
    location: '',
    issueDescription: ''
  };

  showSuccessPopup = false;
  successMessage = '';

  // Per-card loading state for the firm "Book Appointment" prefetch,
  // and a dedicated flag for the lead-capture popup submit — neither
  // should block the whole page behind the full-screen overlay.
  loadingFirmId: string | null = null;
  isSubmittingLead = false;

  constructor(public router: Router, private route: ActivatedRoute, private auth: AuthService) { }

  ngOnInit() {
    const token = localStorage.getItem('TOKEN');
    this.isLoggedIn = !!token;
    const specializationsReady = this.loadSpecializations();

    if (this.isLoggedIn) {
      this.profileImage = localStorage.getItem('profileImg') || '';
      this.firstName = localStorage.getItem('first_name') || '';
    }

    // ── Inbound deep link (e.g. from the WordPress landing page):
    //    /search-results?location=Mumbai&practice_area=Family%20Law
    const qp = this.route.snapshot.queryParamMap;
    const qpLocation = (qp.get('location') || qp.get('city') || '').trim();
    const qpPracticeArea = (qp.get('practice_area') || qp.get('specialization') || '').trim();

    if (qpLocation || qpPracticeArea) {
      this.runDeepLinkSearch(qpLocation, qpPracticeArea, specializationsReady);
      return;
    }

    const navState = history.state;
    const apiResponse = navState?.lawyers;
    // console.log('navState', navState)
    // console.log('apiResponse', apiResponse)

    if (apiResponse && apiResponse.lawyers) {
      // ── Fresh navigation from home / filter ──
      // NOTE: history.state (set via router navigate `state:`) persists across a
      // hard refresh because it's stored on the browser's session-history entry,
      // not in JS memory. Without clearing it here, refreshing this page keeps
      // seeing this same stale `apiResponse` and skips the API call entirely,
      // never reaching the sessionStorage-based refetch below. Clearing it right
      // after use ensures a subsequent refresh falls through to the "else"
      // branch and re-triggers the search API as intended.
      history.replaceState(null, '', window.location.href);

      this.allLawyers = this.transformLawyersData(apiResponse.lawyers);
      this.lawyers = [...this.allLawyers];
      this.totalCount = apiResponse.count ?? this.lawyers.length;
      this.relNextCursor = apiResponse?.nextCursor;
      this.relPrevCursor = apiResponse?.prevCursor;
      this.initialSearchCriteria = apiResponse.searchCriteria;

      // Bug 1 fix: prefer explicit city passed from home, then searchCriteria, then first lawyer address
      this.filters.location =
        navState.city ||
        apiResponse.searchCriteria?.city ||
        apiResponse.lawyers?.[0]?.address?.city ||
        '';

      this.filters.searchQuery = navState.specialization || '';
      // Mark as selected so blur doesn't wipe it
      if (this.filters.searchQuery) {
        this.selectedSpecialization = { name: this.filters.searchQuery };
      }

      this.populateFilterOptions();

      // Bug 2 fix: persist search params to sessionStorage for refresh/back recovery
      sessionStorage.setItem('sr_city', this.filters.location);
      sessionStorage.setItem('sr_specialization', this.filters.searchQuery);
      sessionStorage.removeItem('sr_cursor');

    } else {
      // Bug 2 fix: no history.state (refresh or back/forward) — re-run last search
      const cachedCity = sessionStorage.getItem('sr_city') || '';
      const cachedSpec = sessionStorage.getItem('sr_specialization') || '';
      const cachedCursor = sessionStorage.getItem('sr_cursor') || '';

      if (cachedCity) {
        this.filters.location = cachedCity;
        this.filters.searchQuery = cachedSpec;
        if (cachedSpec) {
          this.selectedSpecialization = { name: cachedSpec };
        }
        this.isLoading = true;
        const params: any = { city: cachedCity };
        if (cachedSpec) params.specialization = cachedSpec;
        if (cachedCursor) params.cursor = cachedCursor;

        this.auth.lawyerPageSearch(params).subscribe({
          next: (res: any) => {
            this.isLoading = false;
            this.allLawyers = this.transformLawyersData(res.lawyers || []);
            this.lawyers = [...this.allLawyers];
            this.totalCount = res.count ?? this.lawyers.length;
            this.relNextCursor = res?.nextCursor;
            this.relPrevCursor = res?.prevCursor;
            this.populateFilterOptions();
          },
          error: () => { this.isLoading = false; }
        });
      }
    }
  }

  // ── Deep link from WordPress landing page ────────────────────────────────
  private runDeepLinkSearch(location: string, practiceArea: string, specializationsReady: Promise<void>) {
    this.filters.location = location;
    this.isLoading = true;

    // Wait for the specialization list so the incoming value (which may be a
    // slug like "family-law") can be resolved to the canonical name the API expects.
    specializationsReady.then(() => {
      const specialization = practiceArea ? this.resolveSpecialization(practiceArea) : '';

      this.filters.searchQuery = specialization;
      if (specialization) {
        this.selectedSpecialization = { name: specialization };
      }

      const params: any = {};
      if (location) params.city = location;
      if (specialization) params.specialization = specialization;

      sessionStorage.setItem('sr_city', location);
      sessionStorage.setItem('sr_specialization', specialization);
      sessionStorage.removeItem('sr_cursor');

      this.auth.lawyerPageSearch(params).subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.allLawyers = this.transformLawyersData(res.lawyers || []);
          this.lawyers = [...this.allLawyers];
          this.totalCount = res.count ?? this.lawyers.length;
          this.relNextCursor = res?.nextCursor;
          this.relPrevCursor = res?.prevCursor;
          this.populateFilterOptions();
          this.applySorting();
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Deep link search error:', err);
        }
      });
    });
  }

  // Match an incoming practice_area value ("family-law", "FAMILY LAW",
  // "Family Law") against the canonical specialization list.
  private resolveSpecialization(value: string): string {
    const key = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const incoming = key(value);
    if (!incoming) return '';

    const exact = this.popularSpecializations.find(s => key(s.name) === incoming);
    if (exact) return exact.name;

    const partial = this.popularSpecializations.find(
      s => key(s.name).includes(incoming) || incoming.includes(key(s.name))
    );
    if (partial) return partial.name;

    // Unknown value — pass through a readable version and let the API decide.
    return value.replace(/[-_]+/g, ' ').trim();
  }

  // ── Computed: check if any firm in list ──────────────────────────────────
  get hasAnyFirm(): boolean {
    return this.lawyers.some(l => l.isFirm);
  }

  // ── Transform (extended for firms) ──────────────────────────────────────
  transformLawyersData(apiLawyers: any[]): LawyerDisplay[] {
    return apiLawyers.map(lawyer => ({
      id: lawyer.id,
      lawyer_id: lawyer.lawyer_id,
      name: lawyer.name,
      avatar: lawyer.profilePicUrl,
      specialty: this.formatSpecialty(lawyer.specializations),
      bio: lawyer.bio || '',
      rating: this.generateRating(lawyer.yearsOfExperience),
      reviews: this.generateReviews(lawyer.yearsOfExperience),
      experience: lawyer.yearsOfExperience,
      location: this.formatLocation(lawyer.address),
      languages: this.formatLanguages(lawyer.language || []),
      availability: lawyer.availability,
      consultationFee: parseFloat(lawyer.consultation_fee ?? lawyer.consultationFee ?? '0') || 0,
      availabilityStyle: this.getAvailabilityStyle(lawyer.availability),
      specializations: lawyer.specializations || [],
      isAvailableToday: lawyer.isAvailableToday ?? false,
      isAvailableTomorrow: lawyer.isAvailableTomorrow ?? false,
      isAvailableThisWeek: lawyer.isAvailableThisWeek ?? false,
      isSubActive: lawyer.isSubActive ?? false,

      // NEW: firm fields — safe fallback to false
      isFirm: lawyer.isFirm === true,
      memberCount: lawyer.memberCount ?? null,
      firmId: lawyer.firm_id ?? lawyer.id,
      address: lawyer.address,
      firmExperience: lawyer.firmExperience ?? lawyer.yearsOfExperience ?? null,
    }));
  }

  // ── NEW: Format spec chip label ─────────────────────────────────────────
  formatSpecChip(spec: string): string {
    return spec
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  // ── NEW: Navigate to firm details ────────────────────────────────────────
  viewFirm(firm: LawyerDisplay) {
    if (!firm.isSubActive) {
      this.selectedLawyer = firm;
      this.showSubscriptionPopup = true;
      return;
    }
    this.router.navigate(['/firm', firm.firmId || firm.id], {
      state: { firm }
    });
  }

  populateFilterOptions() {
    const expertiseSet = new Set<string>();
    const languageSet = new Set<string>();

    this.allLawyers.forEach(lawyer => {
      lawyer.specializations.forEach(area => expertiseSet.add(this.formatExpertiseName(area)));
      if (lawyer.languages) {
        lawyer.languages
          .split(',')
          .map(lang => lang.trim())
          .filter(lang => lang !== '')
          .forEach(lang => languageSet.add(lang));
      }
    });

    this.availableExpertise = Array.from(expertiseSet).sort();
    this.availableLanguages = Array.from(languageSet).sort();
  }

  formatExpertiseName(expertise: string): string {
    return expertise
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatSpecialty(specializations: string[]): string {
    if (specializations && specializations.length > 0) {
      return specializations
        .map(spec =>
          spec.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        )
        .join(', ');
    }
    return 'General Practice';
  }

  formatLocation(address: any): string {
    if (!address) return '';
    const parts = [address.city, address.state].filter(Boolean);
    return parts.join(', ');
  }

  formatLanguages(languages: string[]): string {
    return languages
      .map(lang => lang.charAt(0).toUpperCase() + lang.slice(1))
      .join(', ');
  }

  getAvailabilityStyle(availability: string): { background: string; color: string } {
    if (!availability || availability.toLowerCase() === 'unavailable') {
      return { background: '#e5e7eb', color: '#6b7280' };
    }
    return { background: '#d1fae5', color: '#065f46' };
  }

  generateRating(years: number): number {
    return Math.min(5, 3.5 + (years || 0) * 0.1);
  }

  generateReviews(years: number): number {
    return Math.floor((years || 1) * 12);
  }

  loadSpecializations(): Promise<void> {
    return fetch(environment.profapi)
      .then(res => res.json())
      .then((data: any) => {
        const rawList: string[] = data?.data || [];
        const sortedList = rawList.sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
        this.popularSpecializations = sortedList.map((name: string) => ({
          name,
          type: 'SPECIALITY'
        }));
        this.filteredSpecializations = [...this.popularSpecializations];
      })
      .catch(() => {
        this.popularSpecializations = [];
        this.filteredSpecializations = [];
      });
  }

  formatSpecializationsWithMore(specializations: string[]): string {
    if (!specializations || specializations.length === 0) {
      return 'General Practice';
    }

    const displaySpecs = specializations.slice(0, 5);
    return displaySpecs
      .map(spec => this.formatSpecChip(spec))
      .join(', ');
  }

  onApplyFilters() {
    this.isLoading = true;
    const specialization = this.selectedSpecialization?.name || this.filters.searchQuery;
    this.auth.lawyerPageSearch({
      city: this.filters.location,
      specialization,
    }).subscribe(
      (res: any) => {
        console.log('Filter response:', res);
        this.isLoading = false;
        this.allLawyers = this.transformLawyersData(res.lawyers);
        this.lawyers = this.applyClientSideFilters(this.allLawyers);
        this.totalCount = res.count ?? this.lawyers.length;
        this.relNextCursor = res?.nextCursor;
        this.relPrevCursor = res?.prevCursor;
        this.applySorting();
        // Persist updated search params
        sessionStorage.setItem('sr_city', this.filters.location);
        sessionStorage.setItem('sr_specialization', specialization);
        sessionStorage.removeItem('sr_cursor');

        // Keep the URL shareable / refresh-safe
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            location: this.filters.location || null,
            practice_area: specialization || null
          },
          replaceUrl: true
        });
      },
      error => {
        this.isLoading = false;
        console.error('Filter error:', error);
      }
    );
  }

  applyClientSideFilters(lawyers: LawyerDisplay[]): LawyerDisplay[] {
    return lawyers.filter(lawyer => {
      let matches = true;

      if (this.advancedFilters.expertise) {
        matches = matches && lawyer.specializations.some(s =>
          this.formatExpertiseName(s).toLowerCase().includes(this.advancedFilters.expertise.toLowerCase())
        );
      }

      if (this.advancedFilters.language) {
        matches = matches && (lawyer.languages || '').toLowerCase().includes(this.advancedFilters.language.toLowerCase());
      }

      if (this.advancedFilters.fees && !lawyer.isFirm) {
        const fee = lawyer.consultationFee;
        if (this.advancedFilters.fees === 'Under ₹500') matches = matches && fee < 500;
        else if (this.advancedFilters.fees === '₹500 - ₹1000') matches = matches && fee >= 500 && fee <= 1000;
        else if (this.advancedFilters.fees === '₹1000 - ₹2000') matches = matches && fee > 1000 && fee <= 2000;
        else if (this.advancedFilters.fees === 'Above ₹2000') matches = matches && fee > 2000;
      }

      if (this.advancedFilters.availability) {
        if (this.advancedFilters.availability === 'Available Today') matches = matches && lawyer.isAvailableToday;
        else if (this.advancedFilters.availability === 'Available Tomorrow') matches = matches && lawyer.isAvailableTomorrow;
        else if (this.advancedFilters.availability === 'Available This Week') matches = matches && lawyer.isAvailableThisWeek;
      }

      return matches;
    });
  }

  onClearFilters() {
    const currentLocation = this.filters.location;
    const currentspl = this.filters.searchQuery;
    this.filters = { searchQuery: currentspl, location: currentLocation, experience: '', rating: '' };
    this.advancedFilters = { expertise: '', language: '', fees: '', availability: '' };
    this.lawyers = [...this.allLawyers];
    this.applySorting();
  }

  applySorting() {
    if (this.sortOption === 'Highest Rated') this.lawyers.sort((a, b) => b.rating - a.rating);
    else if (this.sortOption === 'Most Experienced') this.lawyers.sort((a, b) => b.experience - a.experience);
    else if (this.sortOption === 'Lowest Fees') this.lawyers.sort((a, b) => a.consultationFee - b.consultationFee);
  }

  onSortChange() { this.applySorting(); }

  onSearchFocus() {
    this.filteredSpecializations = [...this.popularSpecializations];
    this.showSpecializationDropdown = true;
  }

  onSearchInput(event: any) {
    const value = event.target.value.toLowerCase().trim();
    this.selectedSpecialization = null;
    this.filteredSpecializations = value
      ? this.popularSpecializations.filter(spec => spec.name.toLowerCase().includes(value))
      : [...this.popularSpecializations];
    this.showSpecializationDropdown = this.filteredSpecializations.length > 0;
  }

  selectSpecialization(spec: any) {
    this.filters.searchQuery = spec.name;
    this.selectedSpecialization = spec;
    this.showSpecializationDropdown = false;
  }

  onSearchBlur() {
    setTimeout(() => {
      this.showSpecializationDropdown = false;
      if (!this.selectedSpecialization) {
        this.filters.searchQuery = '';
        this.filterLawyers();
      }
    }, 200);
  }

  filterLawyers() {
    const query = this.filters.searchQuery?.toLowerCase().trim();
    if (!query) {
      this.lawyers = this.applyClientSideFilters(this.allLawyers);
      this.applySorting();
      return;
    }
    this.lawyers = this.applyClientSideFilters(this.allLawyers);
    this.applySorting();
  }

  bookSlot(lawyer: any) {
    //console.log('Bookkkkkssssloot', lawyer)
    if (lawyer.availability === 'Unavailable') return;

    // isFirm: call search API with firmId first, then navigate to firm-details
    if (lawyer.isFirm) {
      if (!lawyer.isSubActive) {
      this.selectedLawyer = lawyer;
      this.showSubscriptionPopup = true;
      return;
    }
      const firmId = lawyer.firmId || lawyer.id;
      if (this.loadingFirmId) return; // guard against double-click while a prefetch is already in flight
      this.loadingFirmId = firmId;
      this.auth.lawyerPageSearch({ firmId }).subscribe({
        next: (res: any) => {
          this.loadingFirmId = null;
          const firmLawyers: any[] = (res?.lawyers || []).filter((l: any) => !l.isFirm);
          this.router.navigate(['/firm', firmId], {
            state: { firm: lawyer, firmLawyers }
          });
        },
        error: () => {
          this.loadingFirmId = null;
          // Navigate even if prefetch fails — firm-details will retry on its own
          this.router.navigate(['/firm', firmId], {
            state: { firm: lawyer }
          });
        }
      });
      return;
    }

    // If subscription inactive show popup
    if (!lawyer.isSubActive) {
      this.selectedLawyer = lawyer;
      this.showSubscriptionPopup = true;
      return;
    }

    // Existing lawyer flow — untouched
    this.router.navigate(['/bookslot'], { state: { lawyerId: lawyer.lawyer_id } });
  }

  login() { this.router.navigate(['/login']); }
  toggleMobileMenu() { this.mobileMenuOpen = !this.mobileMenuOpen; }

  getFirstName(name: string): string {
    if (!name) return '';
    return name.trim().split(' ')[0];
  }

  getFirstNameChar(name: string): string {
    if (!name) return '';
    return name.trim().split(' ')[0].charAt(0).toUpperCase();
  }

  next() {
    if (!this.relNextCursor) return;
    this.isLoading = true;
    const specialization = this.selectedSpecialization?.name || this.filters.searchQuery;
    this.auth.lawyerPageSearch({
      city: this.filters.location,
      cursor: this.relNextCursor,
      specialization,
    }).subscribe(
      (res: any) => {
        this.isLoading = false;
        this.allLawyers = this.transformLawyersData(res.lawyers);
        this.lawyers = this.applyClientSideFilters(this.allLawyers);
        this.totalCount = res.count ?? this.lawyers.length;
        this.applySorting();
        this.relNextCursor = res?.nextCursor;
        this.relPrevCursor = res?.prevCursor;
        sessionStorage.setItem('sr_cursor', this.relNextCursor || '');
        setTimeout(() => {
          this.resultsListRef?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);
      },
      error => {
        this.isLoading = false;
        console.error('Pagination error:', error);
      }
    );
  }

  prev() {
    if (!this.relPrevCursor) return;
    this.isLoading = true;
    const specialization = this.selectedSpecialization?.name || this.filters.searchQuery;
    this.auth.lawyerPageSearch({
      city: this.filters.location,
      specialization,
      cursor: this.relPrevCursor,
    }).subscribe(
      (res: any) => {
        this.isLoading = false;
        this.allLawyers = this.transformLawyersData(res.lawyers);
        this.lawyers = this.applyClientSideFilters(this.allLawyers);
        this.totalCount = res.count ?? this.lawyers.length;
        this.applySorting();
        this.relNextCursor = res?.nextCursor;
        this.relPrevCursor = res?.prevCursor;
        sessionStorage.setItem('sr_cursor', this.relPrevCursor || '');
        setTimeout(() => {
          this.resultsListRef?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);
      },
      error => {
        this.isLoading = false;
        console.error('Pagination error:', error);
      }
    );
  }


  closeSubscriptionPopup() {
    this.showSubscriptionPopup = false;
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;

    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
  preventLeadingSpace(event: KeyboardEvent) {
    const value = this.leadForm.issueDescription || '';

    if (event.key === ' ' && value.length === 0) {
      event.preventDefault();
    }
  }

  closeSuccessPopup() {
    this.showSuccessPopup = false;

    // Reset form values
    this.leadForm = {
      name: '',
      email: '',
      mobile: '',
      location: '',
      issueDescription: ''
    };
  }

  submitSubscriptionRequest(form: NgForm) {
    if (form.invalid) {
      Object.values(form.controls).forEach(control => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isSubmittingLead) return; // guard against double-submit

    const payload = {
      city: this.leadForm.location,
      email: this.leadForm.email,
      firmname: this.leadForm.name,
      issue: this.leadForm.issueDescription,
      phone: this.leadForm.mobile
    };
    //console.log('Payload:', payload);

    this.isSubmittingLead = true;

    this.auth.findLawyer(payload).subscribe({
      next: (res: any) => {
        //console.log('Response:', res);
        this.isSubmittingLead = false;

        if (res?.status === 200) {
          this.showSubscriptionPopup = false;

          this.successMessage = 'We have received your information and will contact you shortly.';

          this.showSuccessPopup = true;
        }
        else if (res?.status === 400) {
          this.showSubscriptionPopup = false;
          this.successMessage = 'You have already submitted your information. We will contact you shortly.';
          this.showSuccessPopup = true;
        }

      },
      error: (err) => {
        console.error('Error:', err);
        this.isSubmittingLead = false;
      }
    });
  }

}